import {useNavigation, useRoute, RouteProp} from '@react-navigation/native';
import React, {useEffect, useRef, useState} from 'react';
import {
  Alert,
  ImageBackground,
  Keyboard,
  Text,
  TextInput,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import FontAwesome6 from 'react-native-vector-icons/FontAwesome6';
import ButtonComponent from '../../../components/ButtonComponent/ButtonComponent';
import {Colors} from '../../../constants/Colors';
import AuthService from '../../../services/AuthService/AuthService';
import {styles} from '../TokenScreen/styles';
import {NavigationProps} from '../../../navigation/Routes';
import {AppRoutes} from '../../../navigation/Routes.types';
import {RootStackParamList} from '../../../navigation/Routes.types';
import {useLoadingStore} from '../../../store/LoadingStore';
import { showSuccess, showInfo } from '../../../utils/globalAlert';

const ResetTokenScreen = () => {
  const {navigate, goBack} = useNavigation<NavigationProps>();
  const route = useRoute<RouteProp<RootStackParamList, AppRoutes.RESET_TOKEN>>();
  const user = route.params?.user ?? '';

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(60);
  const [resendChannel, setResendChannel] = useState<'whatsapp' | 'email'>('whatsapp');
  const [resendingChannel, setResendingChannel] = useState<'whatsapp' | 'email' | null>(null);
  const {hideLoading, showLoading} = useLoadingStore();

  const inputRefs = [
    useRef<TextInput>(null),
    useRef<TextInput>(null),
    useRef<TextInput>(null),
    useRef<TextInput>(null),
    useRef<TextInput>(null),
    useRef<TextInput>(null),
  ];

  useEffect(() => setTimer(60), []);
  useEffect(() => {
    const t = timer > 0 && setInterval(() => setTimer(timer - 1), 1000);
    return () => { t && clearInterval(t); };
  }, [timer]);

  const formatTimer = () => {
    if (timer === 60) return '1:00';
    return `0:${String(timer).padStart(2, '0')}`;
  };

  const handleResendToken = async (channel: 'whatsapp' | 'email') => {
    showLoading();
    setResendingChannel(channel);
    try {
      const response = await AuthService.resendResetTokenPost(user, channel);
      const retryAfter = response?.retry_after_seconds ?? 60;
      setTimer(retryAfter);
      setResendChannel(channel);
      const channelLabel = channel === 'whatsapp' ? 'WhatsApp' : 'E-mail';
      showSuccess('Conecta Brasal', `Token reenviado! Confira o ${channelLabel}.`);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.details ||
        error?.response?.data?.message ||
        'Erro ao reenviar. Tente novamente.';
      Alert.alert('Opss...', String(errorMessage));
    } finally {
      setResendingChannel(null);
      hideLoading();
    }
  };

  const handleInputChange = (text: string, index: number) => {
    const newCode = [...code];
    if (text.length === 6) {
      text.split('').forEach((digit, i) => { if (i < newCode.length) newCode[i] = digit; });
      setCode(newCode);
      inputRefs[5].current?.focus();
      return;
    }
    if (text.length === 1) {
      newCode[index] = text;
      setCode(newCode);
      if (index < inputRefs.length - 1) inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyPress = (e: any, index: number) => {
    if (e.nativeEvent.key === 'Backspace') {
      if (code[index] !== '') {
        const newCode = [...code]; newCode[index] = ''; setCode(newCode);
      } else if (index > 0) {
        const newCode = [...code]; newCode[index - 1] = ''; setCode(newCode);
        inputRefs[index - 1].current?.focus();
      }
    }
  };

  const validateResetToken = async () => {
    const token = code.join('');
    if (token.length < 6 || code.some(d => d === '')) {
      showInfo('Atenção', 'Por favor, preencha todos os 6 dígitos do código.');
      return;
    }
    showLoading();
    try {
      await AuthService.validateResetTokenPost(user, token);
      navigate(AppRoutes.NEW_PASSWORD, {user, token});
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.details ||
        error?.response?.data?.message ||
        'Token inválido ou expirado.';
      Alert.alert('Opss...', errorMessage);
    } finally {
      hideLoading();
    }
  };

  return (
    <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
      <ImageBackground style={styles.imageBackground}>
        <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
          <View style={styles.container}>
            <TouchableOpacity onPress={goBack} style={styles.arrowButton}>
              <FontAwesome6 name="arrow-left" color={Colors.GrayPallete.lightgrayThree} size={22} />
            </TouchableOpacity>
            <Text style={styles.title}>Código de recuperação</Text>
            <Text style={styles.description}>
              Enviamos um código de 6 dígitos para seus canais de contato cadastrados
              ({resendChannel === 'email' ? 'E-mail' : 'WhatsApp'}). Ele é válido por 30 minutos.
              Digite abaixo para redefinir sua senha.
            </Text>
            <View style={styles.containerBox}>
              {code.map((digit, index) => (
                <TextInput
                  key={index}
                  ref={inputRefs[index]}
                  style={styles.containerTextBox}
                  placeholder="*"
                  maxLength={6}
                  placeholderTextColor="lightgray"
                  keyboardType="numeric"
                  value={digit}
                  onChangeText={(text) => handleInputChange(text, index)}
                  onKeyPress={(e) => handleKeyPress(e, index)}
                />
              ))}
            </View>
            <Text
              style={{
                width: '100%',
                textAlign: 'center',
                fontFamily: 'TCCC-UnityHeadline Regular',
              }}>
              {timer > 0
                ? `Você poderá solicitar um novo código em ${formatTimer()}.`
                : 'Você já pode solicitar um novo código.'}
            </Text>
            <ButtonComponent title="Validar" onPress={validateResetToken} />
            {timer === 0 && (
              <View style={{marginTop: 20, width: '100%'}}>
                <Text
                  style={{
                    width: '100%',
                    textAlign: 'center',
                    marginBottom: 12,
                    fontFamily: 'TCCC-UnityHeadline Regular',
                  }}>
                  Não recebeu? Escolha como deseja receber o novo código:
                </Text>
                <ButtonComponent
                  title={resendingChannel === 'whatsapp' ? 'Enviando...' : 'Reenviar por WhatsApp'}
                  iconName="whatsapp"
                  onPress={() => handleResendToken('whatsapp')}
                  disabled={!!resendingChannel}
                  isLoading={resendingChannel === 'whatsapp'}
                />
                <View style={{height: 12}} />
                <ButtonComponent
                  title={resendingChannel === 'email' ? 'Enviando...' : 'Reenviar por E-mail'}
                  iconName="envelope"
                  onPress={() => handleResendToken('email')}
                  disabled={!!resendingChannel}
                  isLoading={resendingChannel === 'email'}
                />
              </View>
            )}
          </View>
        </TouchableWithoutFeedback>
      </ImageBackground>
    </TouchableWithoutFeedback>
  );
};

export default ResetTokenScreen;
